/**
 * Celebration Confetti & Sparkles Utility
 * Provides vibrant full-screen fireworks, star sparkles, and confetti bursts
 * when a user clicks on a festival date.
 */

const RAINBOW_COLORS = [
  '#ff3838', '#ff9f1a', '#ffd32a', '#2ed573', 
  '#1e90ff', '#a55eea', '#ff4757', '#ffa502', 
  '#7bed9f', '#70a1ff', '#e056fd', '#00d2d3',
  '#f368e0', '#ff9ff3', '#54a0ff', '#5f27cd'
];

const SPARKLE_SHAPES = ['circle', 'rect', 'star', 'diamond'];

class Particle {
  constructor(x, y, isBurst = false) {
    this.x = x;
    this.y = y;
    this.color = RAINBOW_COLORS[Math.floor(Math.random() * RAINBOW_COLORS.length)];
    this.shape = SPARKLE_SHAPES[Math.floor(Math.random() * SPARKLE_SHAPES.length)];
    this.size = Math.random() * 10 + 6; // Noticeable, vibrant particle size
    this.alpha = 1;
    this.decay = Math.random() * 0.012 + 0.007; // 2.5 - 4 seconds duration

    if (isBurst) {
      // 360-degree outward radial burst from click position
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 14 + 5;
      this.vx = Math.cos(angle) * speed;
      this.vy = Math.sin(angle) * speed;
      this.gravity = 0.22;
      this.drag = 0.95;
    } else {
      // Confetti cannon from bottom corners shooting upwards
      const angle = (Math.random() * 0.5 + 0.25) * -Math.PI;
      const speed = Math.random() * 20 + 12;
      this.vx = Math.cos(angle) * speed;
      this.vy = Math.sin(angle) * speed;
      this.gravity = 0.32;
      this.drag = 0.98;
    }

    this.rotation = Math.random() * 360;
    this.rotationSpeed = (Math.random() - 0.5) * 16;
    this.wobble = Math.random() * 10;
    this.wobbleSpeed = Math.random() * 0.12 + 0.05;
  }

  update() {
    this.vx *= this.drag;
    this.vy = this.vy * this.drag + this.gravity;
    this.x += this.vx;
    this.y += this.vy;
    this.rotation += this.rotationSpeed;
    this.wobble += this.wobbleSpeed;
    this.alpha -= this.decay;
  }

  draw(ctx) {
    if (this.alpha <= 0) return;
    ctx.save();
    ctx.globalAlpha = Math.max(0, this.alpha);
    ctx.translate(this.x, this.y);
    ctx.rotate((this.rotation * Math.PI) / 180);
    ctx.fillStyle = this.color;
    ctx.strokeStyle = this.color;

    // Glowing sparkle effect
    ctx.shadowBlur = 8;
    ctx.shadowColor = this.color;

    if (this.shape === 'circle') {
      ctx.beginPath();
      ctx.arc(0, 0, this.size / 2, 0, Math.PI * 2);
      ctx.fill();
    } else if (this.shape === 'rect') {
      ctx.fillRect(-this.size / 2, -this.size / 4, this.size, this.size / 2);
    } else if (this.shape === 'diamond') {
      ctx.beginPath();
      ctx.moveTo(0, -this.size / 2);
      ctx.lineTo(this.size / 3, 0);
      ctx.lineTo(0, this.size / 2);
      ctx.lineTo(-this.size / 3, 0);
      ctx.closePath();
      ctx.fill();
    } else if (this.shape === 'star') {
      // 5-point sparkling star
      ctx.beginPath();
      const points = 5;
      const outer = this.size * 0.8;
      const inner = outer / 2.2;
      for (let i = 0; i < points * 2; i++) {
        const r = i % 2 === 0 ? outer : inner;
        const a = (i * Math.PI) / points;
        const sx = Math.cos(a) * r;
        const sy = Math.sin(a) * r;
        if (i === 0) ctx.moveTo(sx, sy);
        else ctx.lineTo(sx, sy);
      }
      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  }
}

let activeCanvas = null;
let animationFrameId = null;
let activeParticles = [];

/**
 * Launch celebration sparkles and confetti across the screen!
 * Safe to call from anywhere, works for all users.
 * @param {number} [originX] - X coordinate of click
 * @param {number} [originY] - Y coordinate of click
 */
export const triggerFestivalCelebration = (originX, originY) => {
  if (typeof window === 'undefined') return;

  const width = window.innerWidth;
  const height = window.innerHeight;

  const clickX = originX !== undefined && originX !== null && !isNaN(originX) ? originX : width / 2;
  const clickY = originY !== undefined && originY !== null && !isNaN(originY) ? originY : height * 0.45;

  // Ensure canvas exists with ultra-high z-index
  let canvas = activeCanvas;
  if (!canvas || !document.body.contains(canvas)) {
    canvas = document.createElement('canvas');
    canvas.id = 'festival-celebration-canvas';
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100vw';
    canvas.style.height = '100vh';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '9999999'; // Highest z-index above all modals and headers
    document.body.appendChild(canvas);
    activeCanvas = canvas;
  }

  canvas.width = width;
  canvas.height = height;

  // 1. Sparkle radial burst directly from click point
  for (let i = 0; i < 75; i++) {
    activeParticles.push(new Particle(clickX, clickY, true));
  }

  // 2. Confetti cannon from bottom left
  for (let i = 0; i < 45; i++) {
    const p = new Particle(width * 0.1, height, false);
    p.vx = Math.abs(p.vx) * 0.85 + 4;
    activeParticles.push(p);
  }

  // 3. Confetti cannon from bottom right
  for (let i = 0; i < 45; i++) {
    const p = new Particle(width * 0.9, height, false);
    p.vx = -Math.abs(p.vx) * 0.85 - 4;
    activeParticles.push(p);
  }

  // Show floating animated banner
  createFloatingBadge(clickX, clickY);

  // If animation is already running, just let it process the added particles
  if (!animationFrameId) {
    const ctx = canvas.getContext('2d');

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      for (let i = activeParticles.length - 1; i >= 0; i--) {
        const p = activeParticles[i];
        p.update();
        p.draw(ctx);
        if (p.alpha <= 0 || p.y > height + 50) {
          activeParticles.splice(i, 1);
        }
      }

      if (activeParticles.length > 0) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        if (canvas && canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
        activeCanvas = null;
        animationFrameId = null;
      }
    };

    animationFrameId = requestAnimationFrame(animate);
  }
};

/**
 * Creates an animated floating celebration emoji badge at the click location
 */
const createFloatingBadge = (x, y) => {
  const badge = document.createElement('div');
  badge.className = 'festival-click-floating-badge';
  badge.innerHTML = '🎉 🌈 ✨ Happy Festival! ✨ 🌈 🎉';
  badge.style.position = 'fixed';
  badge.style.left = `${Math.min(Math.max(x - 120, 20), window.innerWidth - 270)}px`;
  badge.style.top = `${Math.max(y - 50, 40)}px`;
  badge.style.pointerEvents = 'none';
  badge.style.zIndex = '10000000';
  badge.style.background = 'linear-gradient(90deg, #ff3838, #ff9f1a, #ffd32a, #2ed573, #1e90ff, #a55eea)';
  badge.style.color = '#ffffff';
  badge.style.fontWeight = 'bold';
  badge.style.fontSize = '14px';
  badge.style.padding = '8px 20px';
  badge.style.borderRadius = '50px';
  badge.style.boxShadow = '0 8px 30px rgba(0,0,0,0.35)';
  badge.style.transition = 'all 1.4s cubic-bezier(0.16, 1, 0.3, 1)';
  badge.style.transform = 'scale(0.5) translateY(20px)';
  badge.style.opacity = '0';
  badge.style.textShadow = '0 1px 3px rgba(0,0,0,0.6)';

  document.body.appendChild(badge);

  requestAnimationFrame(() => {
    badge.style.transform = 'scale(1.1) translateY(-30px)';
    badge.style.opacity = '1';

    setTimeout(() => {
      badge.style.transform = 'scale(0.9) translateY(-60px)';
      badge.style.opacity = '0';
      setTimeout(() => {
        if (badge.parentNode) badge.parentNode.removeChild(badge);
      }, 500);
    }, 1300);
  });
};
